// Display · IconText
//
// Inline leaf primitive: leading icon + text in one tone. Renders as a single
// inline-flex span; the tone color is applied directly so the icon (with
// `tone="current"`) inherits via CSS `currentColor`.

import type { ReactNode } from "react";
import { Icon } from "@ds/display/Icon";
import type { IconName, IconSize } from "@ds/display/Icon";
import { SPACE, TEXT, TONE } from "@ds/tokens/palette";
import type { ToneName } from "@ds/tokens/palette";

export type IconTextTone =
  | ToneName
  | "primary"
  | "default"   // alias for "primary"
  | "muted"
  | "faint";

export interface IconTextInput {
  icon?: IconName;
  iconSize?: IconSize;
  /** Tone applied to icon AND text. Defaults to `"muted"`. */
  tone?: IconTextTone;
  children?: ReactNode;
  /**
   * Rendered when `children` is `null`, `undefined`, or `""`.
   * Numbers (e.g. `children={0}`) are NOT treated as empty.
   * Defaults to `"—"` (em dash).
   */
  emptyText?: string;
  testId?: string;
}

export type IconTextProps = IconTextInput;

function resolveColor(tone: IconTextTone): string {
  if (tone === "muted")                       return TEXT.muted;
  if (tone === "faint")                       return TEXT.faint;
  if (tone === "default" || tone === "primary") return TEXT.primary;
  return TONE[tone].text;
}

export function IconText({
  icon,
  iconSize = "sm",
  tone = "muted",
  children,
  emptyText = "—",
  testId,
}: IconTextProps) {
  const isEmpty = children === null || children === undefined || children === "";
  const content = isEmpty ? emptyText : children;

  return (
    <span
      data-testid={testId}
      style={{
        display:    "inline-flex",
        alignItems: "center",
        gap:        SPACE.xs,
        color:      resolveColor(tone),
      }}
    >
      {icon && <Icon name={icon} size={iconSize} tone="current" />}
      {content}
    </span>
  );
}
